import { useState } from 'react'

import { CommandKind, type EngineState } from '@/lib/types'
import { useAppStore } from '@/store/useAppStore'
import { useNow } from '@/hooks/useNow'
import { formatUptime } from '@/lib/format'
import { lifecycleLabel } from './model'
import { Banners } from './Banners'
import { Toggle } from '@/components/common/Toggle'
import { Spinner } from '@/components/common/Spinner'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { BotApiActivity, ModeBadge } from '@/components/layout/Indicators'
import { PlayIcon, StopIcon, PowerIcon } from '@/components/common/icons'
import type { AutobotHook } from './useAutobot'

interface ControlPanelProps {
  engine: EngineState
  run: AutobotHook['run']
  pending: string | null
}

export function ControlPanel({ engine, run, pending }: ControlPanelProps) {
  const nowMs = useNow(1000)
  const [killOpen, setKillOpen] = useState(false)
  const dismissAlert = useAppStore((s) => s.dismissAlert)
  const alerts = useAppStore((s) => s.alerts)

  const lc = engine.lifecycle
  const running = lc === 'running'
  const transitioning = lc === 'starting' || lc === 'stopping'
  const openTrades = engine.active_trades.length

  const dotCls =
    running ? 'status-dot--on' : transitioning ? 'status-dot--err' : 'status-dot--off'

  const killBusy = pending === 'kill'

  return (
    <div className="card card--pad control">
      <div className="control__status">
        <div className="control__statusmain">
          <span className={`status-dot ${dotCls}`} />
          <span className="control__statuslabel">{lifecycleLabel(lc)}</span>
          <ModeBadge mode={engine.mode} />
        </div>
        <div className="control__meta">
          <span className="control__metaitem">
            Аптайм: <strong className="tnum">{formatUptime(engine.started_ts_ms, nowMs)}</strong>
          </span>
          <span className="control__metaitem">
            Открыто связок: <strong className="tnum">{openTrades}</strong>
          </span>
          <BotApiActivity count={engine.exchange_request_count} />
        </div>
      </div>

      <div className="control__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={running || transitioning || pending === 'start'}
          onClick={() => void run('start', CommandKind.START)}
        >
          {pending === 'start' ? <Spinner size={15} className="btn__spin" /> : <PlayIcon size={16} />}
          Старт
        </button>
        <button
          type="button"
          className="btn"
          disabled={!running || pending === 'stop'}
          onClick={() => void run('stop', CommandKind.STOP)}
          title="Прекратить поиск/входы; открытые связки продолжают обслуживаться до закрытия"
        >
          {pending === 'stop' ? <Spinner size={15} className="btn__spin" /> : <StopIcon size={14} />}
          Стоп
        </button>
        <button
          type="button"
          className="btn btn--danger"
          disabled={killBusy || (!running && openTrades === 0)}
          onClick={() => setKillOpen(true)}
          title="Аварийно закрыть все связки (kill-switch) и остановить торговлю"
        >
          {killBusy ? <Spinner size={15} className="btn__spin" /> : <PowerIcon size={16} />}
          Аварийно закрыть всё
        </button>

        <div className="control__toggle">
          <Toggle
            id="entry-gate"
            checked={engine.entry_enabled}
            disabled={!running || pending === 'entry'}
            onChange={(v) => void run('entry', CommandKind.TOGGLE_ENTRY, { enabled: v })}
            label="Входить в новые сделки"
          />
          <label htmlFor="entry-gate" className="control__togglelabel">
            Входить в новые сделки
          </label>
        </div>
      </div>

      <Banners
        engine={engine}
        alerts={alerts}
        onDismissAlert={dismissAlert}
        onResume={() => void run('resume', CommandKind.RESUME_AFTER_SAFE_STARTUP)}
        resumePending={pending === 'resume'}
      />

      <ConfirmDialog
        open={killOpen}
        danger
        title="Аварийно закрыть всё?"
        message={
          <>
            Будет выполнен <strong>kill-switch</strong>: все открытые связки приводятся к
            нейтрали (reduce-only), новые входы выключаются, торговля останавливается. Это
            действие нельзя отменить.
            {openTrades > 0 ? <> Сейчас открыто связок: <strong>{openTrades}</strong>.</> : null}
          </>
        }
        confirmLabel="Закрыть всё"
        busy={killBusy}
        onCancel={() => setKillOpen(false)}
        onConfirm={() => {
          setKillOpen(false)
          void run('kill', CommandKind.EMERGENCY_CLOSE)
        }}
      />
    </div>
  )
}
